import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { useLanguage } from "../../i18n/LanguageContext";
import Sidebar from "../QueryPage/Sidebar";
import { Shield, Users, Activity, DollarSign, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import StatCard from "./StatCard";
import PeriodSelector from "./PeriodSelector";
import TopUsersTable from "./TopUsersTable";
import ConsultationsChart from "./ConsultationsChart";
import CostByModelChart from "./CostByModelChart";
import RegistrationsChart from "./RegistrationsChart";
import UsersEntitlementsTable from "./UsersEntitlementsTable";

const PlatformDashboard = () => {
  const { t } = useLanguage();
  const [period, setPeriod] = useState("month");
  const [stats, setStats] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [consultationsSeries, setConsultationsSeries] = useState([]);
  const [costSeries, setCostSeries] = useState([]);
  const [registrationsSeries, setRegistrationsSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    try {
      const [statsRes, topRes, allRes, consRes, costRes, regRes] = await Promise.all([
        axios.get(`${API}/platform/stats`, { params: { period: p } }),
        axios.get(`${API}/platform/users`, { params: { sort: "cost", limit: 10 } }),
        axios.get(`${API}/platform/users`, { params: { sort: "registered", order: "desc", limit: 100 } }),
        axios.get(`${API}/platform/stats/timeseries`, { params: { metric: "consultations", period: p === "today" ? "week" : p === "all" ? "quarter" : p } }),
        axios.get(`${API}/platform/stats/timeseries`, { params: { metric: "cost", period: p === "today" ? "week" : p === "all" ? "quarter" : p } }),
        axios.get(`${API}/platform/stats/timeseries`, { params: { metric: "registrations", period: p === "today" ? "week" : p === "all" ? "quarter" : p } }),
      ]);
      setStats(statsRes.data?.data);
      setTopUsers(topRes.data?.data?.users || []);
      setAllUsers(allRes.data?.data?.users || []);
      setConsultationsSeries(consRes.data?.data?.data || []);
      setCostSeries(costRes.data?.data?.data || []);
      setRegistrationsSeries(regRes.data?.data?.data || []);
    } catch (err) {
      console.error("Platform data fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(period); }, [period, fetchData]);

  const handlePeriodChange = (p) => setPeriod(p);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-14 md:h-16 border-b border-border flex items-center justify-between px-4 md:px-6 pl-14 md:pl-6 bg-background">
          <div className="flex items-center gap-2 md:gap-3">
            <Shield className="w-4 h-4 md:w-5 md:h-5 text-primary" />
            <h1 className="text-base md:text-lg font-semibold">{t("platform.title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <PeriodSelector value={period} onChange={handlePeriodChange} />
            <Button variant="ghost" size="icon" onClick={() => fetchData(period)} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <StatCard
                label={t("platform.totalUsers")}
                value={stats?.users?.total ?? "—"}
                delta={stats?.users?.new_in_period ? `+${stats.users.new_in_period} ${t("platform.inPeriod")}` : null}
                deltaType="positive"
                icon={<Users className="w-5 h-5" />}
              />
              <StatCard
                label={t("platform.queriesToday")}
                value={stats?.health?.consultations_today ?? "—"}
                icon={<Activity className="w-5 h-5" />}
              />
              <StatCard
                label={t("platform.spendPeriod")}
                value={stats?.tokens?.total_cost_usd != null ? `$${stats.tokens.total_cost_usd.toFixed(2)}` : "—"}
                delta={stats?.tokens?.avg_cost_per_consultation_usd ? `~$${stats.tokens.avg_cost_per_consultation_usd.toFixed(3)}/${t("platform.perQuerySuffix")}` : null}
                deltaType="neutral"
                icon={<DollarSign className="w-5 h-5" />}
              />
              <StatCard
                label={t("platform.errors")}
                value={stats?.health?.error_rate_percent != null ? `${stats.health.error_rate_percent}%` : "—"}
                delta={stats?.health?.avg_latency_seconds ? `Latency: ${stats.health.avg_latency_seconds}s` : null}
                deltaType={stats?.health?.error_rate_percent > 5 ? "negative" : "neutral"}
                icon={<AlertTriangle className="w-5 h-5" />}
              />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <ConsultationsChart data={consultationsSeries} />
              </div>
              <CostByModelChart data={stats?.tokens?.by_model} />
            </div>

            {/* Top users */}
            <TopUsersTable users={topUsers} />

            <UsersEntitlementsTable users={allUsers} onUpdated={() => fetchData(period)} />

            {/* Registrations chart */}
            <RegistrationsChart data={registrationsSeries} authBreakdown={stats?.users?.by_auth_method} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformDashboard;
